import React, {
  useContext,
  useEffect,
  useState,
  useRef
} from 'react';
import { Col, Row } from '@thd-olt-component-react/grid';
import {
  DataProvider, extend, params, string, useDataModel, QueryContext,
  shape, bool, arrayOf
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';
import { useHistory, useLocation, useParams } from '@thd-olt-component-react/router';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { Buybox } from '@thd-olt-component-react/buybox';
import { NonBuyableScheduleAMeasure } from '@thd-olt-component-react/home-services';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { RecommendationsPlaceholder, RecommendationsProvider } from '@thd-olt-component-react/recommendations';
import { QuestionsAndAnswersDesktop } from '@thd-olt-component-react/questions-and-answers';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { RelatedSearch } from '@thd-olt-component-react/related-search';
import { AugmentedReality } from '@thd-olt-component-react/augmented-reality';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { Price } from '@thd-olt-component-react/price';
import { DisclaimerMessage } from '@thd-olt-component-react/disclaimer-message';
import { PromotionProducts } from '@thd-olt-component-react/promotion-products';
import { LayoutGroup, LayoutManager } from '@thd-olt-component-react/layout-manager';
import { SpecificationsDesktop } from '@thd-olt-component-react/specifications';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { Link, Links, StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { SeoLinks } from '@thd-olt-component-react/seo-links';
import { MovingCalculator } from '@thd-olt-component-react/moving-calculator';
import { PIPRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { VirtualConsulting } from '@thd-olt-component-react/virtual-consulting';
import { TextAnExpert } from '@thd-olt-component-react/text-an-expert';
import { PaintDeliveryBanner } from '@thd-olt-component-react/paint-delivery-banner';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { RecsContext } from '~/@thd-olt-component-react/recommendations/dist/components/RecsContext';
import {
  ProductOverviewLoad, ProductOverview, ProductOverviewModel,
  ItemRelatedGroupsLoad, ItemRelatedGroups, ItemRelatedGroupsModel,
  FeatureBasedRecommendationsLoad, FeatureBasedRecommendations, FeatureBasedRecommendationsModel,
  RatingsAndReviewsLoad, RatingsAndReviews, RatingsAndReviewsModel
} from './dynamic-imports';
import {
  shouldDisplayPipInStock,
  shouldDisplayReferrerRecommendations,
  shouldDisplayVisuallySimilar,
  shouldDisplayRequiredItems,
  shouldDisplayScheduleAMeasure
} from './product-utils';
import { HYDRATION_STANDARD_SCROLL_BUFFER } from '../../constants';
import '../../styles/product-page.scss';

const batchedDomains = {
  search: [
    'appliedDimensions',
    'taxonomy',
    'dimensions',
    'metadata',
    'searchReport',
    'products',
    'templates'
  ],

  product: {
    server: [
      'identifiers',
      'info',
      'media',
      'details',
      'badges',
      'reviews',
      'availabilityType',
      'specificationGroup',
      'pricing',
      'taxonomy',
      'keyProductFeatures'
    ],

    client: [
      'identifiers',
      'info',
      'media',
      'details',
      'badges',
      'reviews',
      'availabilityType',
      'specificationGroup',
      'pricing',
      'taxonomy',
      'keyProductFeatures',
      'sizeAndFitDetail',
      'favoriteDetail',
      'installServices',
      'fulfillment'
    ]
  }
};

export const Product = () => {
  declareContexts([RecsContext, QueryContext, ExperienceContext]);
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { channel, segment = '', isDebugMode } = ctx;
  const { itemId } = useParams();
  const storeId = useStoreId();
  const history = useHistory();
  const { pathname } = useLocation();
  const resx = {
    appId: 'homedepot01',
    maxProducts: 16,
    products: itemId,
    showATC: true,
    criteo: {
      version: 'model2'
    },
    event: 'product',
    carouselOptions: {
      fullScrollSkip: 2
    }
  };
  const [recommendationsState, setRecommendationsState] = useState({
    ready: false,
    schema: {
      pipInStock: false,
      pipSponsored: true,
      visuallySimilar_1_0: false,
      required_items: false,
      pipSem: false,
      pipSemMobile: false
    }
  });
  const organicSearchSchema = 'pipsem';

  const arRef = useRef(null);
  const handleARClick = () => {
    arRef.current.click();
  };

  const updateStickyHeader = () => {
    const el = document.querySelector('.Header3');
    if (!el) return;
    el.classList.remove('Header3-sticky-desktop');
  };

  useEffect(() => {
    updateStickyHeader();
  }, []);

  const onChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      history.push(canonicalUrl, { disableScroll: true });
    }
  };

  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  useEffect(() => {
    if (productData) {
      setRecommendationsState({
        ready: true,
        schema: {
          ...recommendationsState.schema,
          pipInStock: shouldDisplayPipInStock(productData),
          pipSem: shouldDisplayReferrerRecommendations(),
          visuallySimilar_1_0: shouldDisplayVisuallySimilar(productData),
          required_items: shouldDisplayRequiredItems(productData)
        }
      });
    }
  }, [productData]);

  const isPaint = segment === 'paint';

  return (
    <DataProvider batchedDomains={batchedDomains} storeId={storeId} itemId={itemId}>
      <LayoutManager name="pip-wrapper" useContextForProps>
        <LayoutGroup name="redirector">
          <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
        </LayoutGroup>
        <LayoutGroup name="product-metadata">
          <Metadata>
            <ProductMetadata itemId={itemId} />
          </Metadata>
        </LayoutGroup>
        <LayoutGroup name="pip-content">
          <RecommendationsProvider resx={resx} ready={recommendationsState.ready}>
            <Row className="isBound">
              <LayoutManager
                data
                useContextForProps
                input={{
                  channel,
                  itemId,
                  isDebugMode,
                  segment
                }}
                name="pip-master"
              >
                <LayoutGroup tag={Row} name="sticknav">
                  <StickyNav itemId={itemId}>
                    <Links>
                      <Link href="#product-overview">Product Overview</Link>
                      <Link href="#product-section-key-feat">Specifications</Link>
                      <Link href="#product-section-qa">Questions & Answers</Link>
                      <Link href="#product-section-rr">Customer Reviews</Link>
                    </Links>
                  </StickyNav>
                </LayoutGroup>
                <LayoutGroup tag={Row} className="u--paddingTop u--paddingBottom" name="breadcrumb-sharebelt-grp">
                  <LayoutManager name="breadcrumb-sharebelt" useContextForProps>
                    <LayoutGroup tag={Col} sm="10" flatten name="breadcrumbs">
                      <Breadcrumbs
                        itemId={itemId}
                      />
                    </LayoutGroup>
                    <LayoutGroup tag={Col} sm="2" flatten name="sharebelt">
                      <Sharebelt horizontal itemId={itemId}>
                        <ShareBeltShareButton email />
                        <ShareBeltPrint />
                      </Sharebelt>
                    </LayoutGroup>
                  </LayoutManager>
                </LayoutGroup>
                {(recommendationsState.schema.pipSem) && (
                  <LayoutGroup tag={Row} id="recs:organic_search" name="recs:organic_search">
                    <Col nopadding>
                      <RecommendationsPlaceholder scheme={organicSearchSchema} hideRecommendation />
                    </Col>
                  </LayoutGroup>
                )}
                <LayoutGroup tag={Row} name="zone-a">
                  <Col nopadding={channel === 'desktop'}>
                    <Row>
                      <Col>
                        <Discontinued itemId={itemId} />
                      </Col>
                      <Col sm="7" flatten className="product--sticky">
                        <div className="zone-card overflow u__clearfix">
                          <LayoutManager name="product-details-share" useContextForProps />
                          <LayoutGroup name="product-info-bar">
                            <Row>
                              <Col>
                                <ProductInfoBar itemId={itemId} padded />
                              </Col>
                            </Row>
                          </LayoutGroup>
                          <LayoutGroup tag={Col} sm="12">
                            <MediaGalleryDesktop
                              itemId={itemId}
                              showLoader
                              onMediaClick={handleARClick}
                            />
                          </LayoutGroup>
                        </div>
                      </Col>
                      <Col sm="5" flatten>
                        <div className="zone-card overflow">
                          <Row>
                            <LayoutManager
                              name="buy-area"
                              useContextForProps
                              input={{
                                channel,
                                segment
                              }}
                            >
                              <LayoutGroup tag={Row} name="product-details-share">
                                <LayoutManager name="product-details-share" useContextForProps>
                                  <LayoutGroup tag={Col} sm="12" name="product-details">
                                    <ProductDetails itemId={itemId}>
                                      <ProductDetails.Badge itemId={itemId} />
                                      <div className="product-details__badge-title--wrapper">
                                        <ProductDetails.Title />
                                      </div>
                                      <ProductDetails.BrandCollection showCollection />
                                      <div className="product-details__favorites">
                                        <Sharebelt itemId={itemId}>
                                          <ShareFavorite showCircle showFavoritesCount />
                                        </Sharebelt>
                                      </div>
                                      <ProductDetails.Ratings
                                        showBadge
                                        target="#product-section-rr"
                                        offsetselector="#sticky-nav"
                                        qaselector="#product-section-qa"
                                      />
                                    </ProductDetails>
                                  </LayoutGroup>
                                </LayoutManager>
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="aisle-bay">
                                <AisleBay itemId={itemId} />
                              </LayoutGroup>
                              <LayoutGroup
                                tag={Col}
                                name="paint-delivery-banner"
                                flatten
                                nopadding
                              >
                                <PaintDeliveryBanner />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="salient">
                                <SalientPoints itemId={itemId} max={3}>
                                  <SalientListItem>
                                    {/* eslint-disable-next-line */}
                                    <a className="u__default-link" href="#product-overview">See More Details</a>
                                  </SalientListItem>
                                </SalientPoints>
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="order-sample">
                                <OrderSample itemId={itemId}>
                                  Need a closer look?
                                </OrderSample>
                              </LayoutGroup>
                              <LayoutGroup tag={Col} flatten={isPaint} name="price">
                                <Price
                                  channel="desktop"
                                  displayEachUom={false}
                                  itemId={itemId}
                                  large
                                  disableRangePricing
                                  skip
                                />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="disclaimer-message">
                                <DisclaimerMessage itemId={itemId} />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="consumer-card-messaging">
                                <ConsumerCardMessaging itemId={itemId} />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="gcc">
                                <GlobalCustomConfigurator itemId={itemId} />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="super-sku">
                                <SuperSku
                                  itemId={itemId}
                                  onChange={onChange}
                                  large={isPaint}
                                  centeredCards={isPaint}
                                  disableMediaAndAvailability={isPaint}
                                />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="moving-calculator">
                                <MovingCalculator itemId={itemId} />
                              </LayoutGroup>
                              <LayoutGroup tag={Col} name="promotion-products">
                                <PromotionProducts itemId={itemId} />
                              </LayoutGroup>
                              {shouldDisplayScheduleAMeasure(productData)
                                ? (
                                  <LayoutGroup tag={Col} name="non-buyable-schedule-a-measure">
                                    <NonBuyableScheduleAMeasure itemId={itemId} />
                                  </LayoutGroup>
                                )
                                : (
                                  <LayoutGroup tag={Col} name="buybox">
                                    <Buybox itemId={itemId} lmUseContextForProps sticky />
                                  </LayoutGroup>
                                )}
                            </LayoutManager>
                          </Row>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </LayoutGroup>
                <LayoutGroup tag={Row} name="augmented-reality">
                  <div className="zone-card">
                    <AugmentedReality
                      itemId={itemId}
                      instructions
                      forwardedRef={arRef}
                    />
                  </div>
                </LayoutGroup>
                <LayoutGroup tag={Row} name="virtual-consulting">
                  <Col nopadding>
                    <VirtualConsulting itemId={itemId} />
                  </Col>
                </LayoutGroup>
                {recommendationsState.schema.pipInStock && (
                  <LayoutGroup tag={Row} name="pipinstock">
                    <Col nopadding>
                      <RecommendationsPlaceholder scheme="pipinstock" />
                    </Col>
                  </LayoutGroup>
                )}
                {recommendationsState.schema.required_items && (
                  <LayoutGroup tag={Row} name="required-items">
                    <Col nopadding>
                      <RecommendationsPlaceholder scheme="required_items" />
                    </Col>
                  </LayoutGroup>
                )}
                <DynamicComponent pageType="pip">
                  <Hydrator
                    id="product-section-fbt"
                    className="grid"
                    delay={1500}
                    scrollBuffer={100}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    preserveCtxVal="clientStore"
                  >
                    <LayoutGroup tag={Row} name="fbt">
                      <div className="zone-card">
                        <FrequentlyBoughtTogether itemId={itemId} qctx={qctx} />
                      </div>
                    </LayoutGroup>
                    <LayoutGroup tag={Row} name="text-an-expert">
                      <TextAnExpert />
                    </LayoutGroup>
                  </Hydrator>

                  <Hydrator
                    id="product-section-overview"
                    className="grid"
                    delay={1500}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={500}
                    waitFor={[ProductOverviewLoad]}
                  >
                    <LayoutGroup tag={Row} name="product-overview" id="product-overview">
                      <div className="zone-card">
                        <ProductOverview itemId={itemId} channel={channel} qctx={qctx} revJet />
                      </div>
                    </LayoutGroup>
                  </Hydrator>

                  <Hydrator
                    id="product-section-irg"
                    className="grid"
                    delay={1500}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={[ItemRelatedGroupsLoad]}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    preserveCtxVal="clientStore"
                  >
                    <LayoutGroup tag={Row} name="IRG">
                      <div className="zone-card">
                        <Col>
                          <ItemRelatedGroups
                            itemId={itemId}
                            maxItems={6}
                            rowHeader="Get Everything You Need"
                            accordion
                            showMobileCardTabs
                            dsNext
                            qctx={qctx}
                          />
                        </Col>
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                </DynamicComponent>
                <LayoutGroup tag={Row} name="article-recommendations">
                  <Col nopadding>
                    <RecommendationsPlaceholder scheme="article_recs" defer />
                  </Col>
                </LayoutGroup>
                <LayoutGroup tag={Row} name="pip_sponsored">
                  <Col nopadding>
                    <RecommendationsPlaceholder scheme="pip_sponsored" defer />
                  </Col>
                </LayoutGroup>
                <DynamicComponent pageType="pip">
                  <Hydrator
                    id="product-section-fbr"
                    className="grid"
                    delay={1500}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={[FeatureBasedRecommendationsLoad]}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    preserveCtxVal="clientStore"
                  >
                    <LayoutGroup tag={Row} name="FBR">
                      <div className="zone-card">
                        <FeatureBasedRecommendations
                          hideOnMobile
                          itemId={itemId}
                          qctx={qctx}
                        />
                      </div>
                    </LayoutGroup>
                  </Hydrator>

                  <Hydrator
                    id="product-section-key-feat"
                    className="grid"
                    delay={1500}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                  >
                    <LayoutGroup tag={Row} name="specifications">
                      <div className="zone-card">
                        <SpecificationsDesktop itemId={itemId} />
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                </DynamicComponent>
                {(recommendationsState.ready && !recommendationsState.schema.pipInStock) && (
                  <LayoutGroup tag={Row} name="alternatives">
                    <Col nopadding>
                      <RecommendationsPlaceholder scheme="pip_alternatives" />
                    </Col>
                  </LayoutGroup>
                )}
                {recommendationsState.schema.visuallySimilar_1_0 && (
                  <LayoutGroup tag={Row} name="visually-similar">
                    <Col className="zone-card">
                      <RecommendationsPlaceholder itemId={itemId} scheme="visuallysimilar_1_0" />
                    </Col>
                  </LayoutGroup>
                )}
                <DynamicComponent pageType="pip">
                  <Hydrator
                    id="product-section-qa"
                    className="grid"
                    delay={2000}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={RatingsAndReviewsLoad}
                  >
                    <LayoutGroup tag={Row} name="questions" id="questions_answers_full" className="zone-card">
                      <QuestionsAndAnswersDesktop itemId={itemId} />
                    </LayoutGroup>
                  </Hydrator>
                  <Hydrator
                    id="product-section-rr"
                    className="grid"
                    delay={2000}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    waitFor={RatingsAndReviewsLoad}
                  >
                    <LayoutGroup tag={Row} name="ratings" id="ratings-and-reviews">
                      <div className="zone-card">
                        <RatingsAndReviews itemId={itemId} prosAndCons qctx={qctx} />
                      </div>
                    </LayoutGroup>
                  </Hydrator>
                </DynamicComponent>
                <LayoutGroup tag={Row} name="recently-viewed">
                  <Col nopadding>
                    <RecommendationsPlaceholder defer scheme="rv_gm_pip_rr" />
                  </Col>
                </LayoutGroup>
                <DynamicComponent pageType="pip">
                  <Hydrator
                    id="product-section-seo"
                    className="grid"
                    scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    delay={2000}
                    placeholder={(
                      <LoadingPlaceholder
                        color="#f5f5f5"
                        ready={false}
                        style={{ height: 750 }}
                        type="rect"
                      >
                        <div />
                      </LoadingPlaceholder>
                    )}
                    preserveCtxVal="clientStore"
                  >
                    <LayoutGroup tag={Row} name="related-search">
                      <Col nopadding>
                        <RelatedSearch itemId={itemId} />
                      </Col>
                    </LayoutGroup>
                    <LayoutGroup tag={Row} name="seo-links">
                      <SeoLinks path={pathname} />
                    </LayoutGroup>
                  </Hydrator>
                </DynamicComponent>
              </LayoutManager>
            </Row>
          </RecommendationsProvider>
        </LayoutGroup>
      </LayoutManager>
    </DataProvider>
  );
};

Product.displayName = 'ProductPage';

Product.batchedDomains = [
  'details',
  'identifiers',
  'info',
  'media',
  'pricing',
  'reviews',
  'taxonomy',
  'specificationGroup'
];

Product.propTypes = {};

Product.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      fulfillment: params({ storeId: string(), zipCode: string() }).shape({
        backordered: bool()
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
          reqItems: bool()
        })
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(shape({
            type: string(),
            locations: arrayOf(shape({
              isAnchor: bool(),
              inventory: shape({
                isLimitedQuantity: bool(),
                isOutOfStock: bool()
              })
            }))
          }))
        })),
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  RecommendationsPlaceholder,
  Discontinued,
  ProductInfoBar,
  MediaGalleryDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  ConsumerCardMessaging,
  DisclaimerMessage,
  GlobalCustomConfigurator,
  OrderSample,
  SuperSku,
  MovingCalculator,
  Buybox,
  NonBuyableScheduleAMeasure,
  AugmentedReality,
  FrequentlyBoughtTogether,
  ProductOverviewModel,
  ItemRelatedGroupsModel,
  FeatureBasedRecommendationsModel,
  SpecificationsDesktop,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsModel,
  SeoLinks,
  RelatedSearch,
  PromotionProducts,
  VirtualConsulting
);
